import {  Button, Grid, Checkbox, Typography, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Slider, TextField} from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { TunaPluginRenderPropertiesProps } from '@tunasong/plugin-lib'
import type { Poll, PollResponse } from '@tunasong/schemas'
import { isManyOfManyPoll, isPoll, pollTypes } from '@tunasong/schemas'
import { EntitySelect } from '@tunasong/tunasong-ui'
import type { ChangeEventHandler, FC } from 'react'
import React from 'react'
import { useCallback } from 'react'
import css from './properties.module.css'

export interface PropertiesProps extends TunaPluginRenderPropertiesProps<Poll> {
  className?: string
}

export const Properties: FC<PropertiesProps> = props => {
  const { className, element, onChange } = props

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } = graphHooks.useEntityChildren<PollResponse>({
    parentId: element.id,
    filter: 'pollresponse',
  })

  const handleClick = useCallback(() => {
    if (!isPoll(element)) {
      return
    }
    const { pollOptions = [] } = element
    const updated = {
      ...element,
      pollOptions: [...pollOptions, { name: '' }],
    }
    onChange(updated)
  }, [element, onChange])

  const updateOption = useCallback(
    (idx: number): ChangeEventHandler<HTMLInputElement> =>
      ({ target: { value: name } }) => {
        if (!isPoll(element)) {
          return
        }
        const pollOptions = [...element.pollOptions]
        pollOptions[idx] = { name }
        const updated = { ...element, pollOptions }
        onChange(updated)
      },
    [element, onChange]
  )

  const updateOptionRef = (idx: number, entityRef?: string) => {
    const pollOptions = [...element.pollOptions]
    pollOptions[idx] = { ...pollOptions[idx], entityRef }
    const updated = { ...element, pollOptions }
    onChange(updated)
  }

  const remove = useCallback(
    (idx: number) => () => {
      if (!isPoll(element)) {
        return
      }
      const pollOptions = [...element.pollOptions]
      pollOptions.splice(idx, 1)
      const updated = { ...element, pollOptions }
      onChange(updated)
    },
    [element, onChange]
  )

  const handleChangeType = useCallback(
    (ev: unknown, pollType: string) => {
      const updated = { ...element, pollType: pollType as never } satisfies Poll
      onChange(updated)
    },
    [element, onChange]
  )

  const handleQuotaChange = useCallback(
    (ev: unknown, value: number | number[]) => {
      const responseQuota = typeof value === 'number' && value > 0 ? value : undefined
      const updated = { ...element, responseQuota }
      onChange(updated)
    },
    [element, onChange]
  )

  const handleRandomize = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const updated = { ...element, randomizeOptions: ev.target.checked }
    onChange(updated)
  }

  if (!isPoll(element) || !hasLoadedChildren) {
    return null
  }
  const { pollOptions = [], pollType } = element

  if (allResponses.length > 0) {
    return <Typography variant="subtitle1">Cannot edit poll with responses</Typography>
  }

  return (
    <div className={[css.root, className].filter(Boolean).join(' ')}>
      <FormControl>
        <RadioGroup aria-label="options" name="options" value={pollType ?? ''} onChange={handleChangeType}>
          {pollTypes.map(value => (
            <FormControlLabel key={value} value={value} control={<Radio />} label={value} />
          ))}
        </RadioGroup>
      </FormControl>

      {isManyOfManyPoll(element) && (
        <>
          <FormLabel className={css.formLabel}>
            Vote quota
          </FormLabel>
          <div className={css.sliderPadding}>
            <Slider
              value={typeof element.responseQuota === 'number' ? element.responseQuota : 0}
              onChange={handleQuotaChange}
              min={0}
              max={element.pollOptions?.length}
            />
          </div>
        </>
      )}

      <FormLabel className={css.formLabel}>
        Options
      </FormLabel>
      <FormControlLabel
        control={<Checkbox checked={element?.randomizeOptions} onChange={handleRandomize} />}
        label="Randomize order of options in poll"
      />
      <Typography variant="caption" className={css.helpText}>
        Add options for users to vote on. You can give the option a name, or optionally link to an entity that
        represents the option (e.g., a song or audio clip)
      </Typography>

      {pollOptions.map((option, idx) => (
        <Grid container key={idx}>
          <Grid size={{ xs: 3 }}>
            <EntitySelect
              buttonText="Link Entity "
              entityId={option.entityRef}
              onSelected={entityRef => updateOptionRef(idx, entityRef)}
            />
          </Grid>
          <Grid size={{ xs: 4 }} className={css.optionGridFlex}>
            {option.entityRef ? (
              <Typography variant="caption" color="primary">
                Linked
              </Typography>
            ) : (
              <TextField
                className={css.optionTextField}
                placeholder="Option"
                defaultValue={option.name}
                onChange={updateOption(idx)}
                variant="standard"
                disabled={Boolean(option.entityRef)}
              />
            )}
          </Grid>
          <Grid size={{ xs: 2 }}>
            {option.entityRef ? <Button onClick={() => updateOptionRef(idx, undefined)}>Clear Ref</Button> : null}
          </Grid>
          <Grid size={{ xs: 2 }}>
            <Button onClick={remove(idx)}>Delete</Button>
          </Grid>
        </Grid>
      ))}
      <Button onClick={handleClick} className={css.createButton}>
        Create option
      </Button>
    </div>
  )
}

export default Properties
